import { useCallback, useEffect, useRef, useState } from "react";
import { useGetIdentity } from "ra-core";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { getActiveViewAsCompany } from "@/modules/platform/viewAsCompanyState";
import type { GridPreferences } from "@/modules/shared/dataGrid/gridTypes";

const localKey = (memberId: unknown, tableId: string) =>
  `nomi.grid.${String(memberId)}.${tableId}`;

const readLocal = (memberId: unknown, tableId: string) => {
  try {
    const raw = window.localStorage.getItem(localKey(memberId, tableId));
    return raw ? (JSON.parse(raw) as GridPreferences) : undefined;
  } catch {
    return undefined;
  }
};

const writeLocal = (
  memberId: unknown,
  tableId: string,
  prefs: GridPreferences,
) => {
  try {
    window.localStorage.setItem(
      localKey(memberId, tableId),
      JSON.stringify(prefs),
    );
  } catch {
    /* storage unavailable */
  }
};

/**
 * A member's columns and filters for one table (member_table_preferences),
 * with a local copy so the table opens as they left it before the server
 * answers. Changes save after a short pause.
 */
export const useGridPreferences = (tableId: string) => {
  const { identity } = useGetIdentity();
  const memberId = identity?.id;
  const [prefs, setPrefsState] = useState<GridPreferences | undefined>(() =>
    memberId != null ? readLocal(memberId, tableId) : undefined,
  );
  const dirty = useRef(false);

  const query = useQuery({
    queryKey: ["grid-preferences", memberId, tableId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("member_table_preferences")
        .select("prefs")
        .eq("member_id", memberId)
        .eq("table_id", tableId)
        .maybeSingle();
      if (error) throw error;
      return (data?.prefs ?? null) as GridPreferences | null;
    },
    enabled: memberId != null,
    staleTime: 5 * 60_000,
    retry: false,
  });

  // Server copy wins unless the member already changed something here.
  useEffect(() => {
    if (memberId == null) return;
    if (query.data !== undefined && !dirty.current) {
      const next = query.data ?? readLocal(memberId, tableId);
      setPrefsState(next ?? undefined);
      if (next) writeLocal(memberId, tableId, next);
    }
  }, [query.data, memberId, tableId]);

  useEffect(() => {
    if (memberId == null || !dirty.current || !prefs) return;
    writeLocal(memberId, tableId, prefs);
    // Viewing as a company: read-only, keep it on this screen only.
    if (getActiveViewAsCompany()) return;
    const timer = window.setTimeout(() => {
      void supabase
        .from("member_table_preferences")
        .upsert(
          {
            member_id: memberId,
            table_id: tableId,
            prefs,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "member_id,table_id" },
        )
        .then(({ error }) => {
          if (error) console.warn("Could not save table preferences", error);
        });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [prefs, memberId, tableId]);

  const setPrefs = useCallback(
    (update: (current: GridPreferences) => GridPreferences) => {
      dirty.current = true;
      setPrefsState((current) => update(current ?? {}));
    },
    [],
  );

  return { prefs, setPrefs, isLoaded: !query.isPending };
};
